import { makeAutoObservable } from "mobx"
import { MouseEvent } from "react"
import { MenuChildItem } from "../MenuConfig"
import { SxProps, Theme } from "@mui/material/styles"
import { notNull } from "@gap/utils/utils"
import RouterStore from "../../RouterStore"
import { MainContainerStore } from "../MainContainerStore"
import { themeConfig } from '@gap/shared/theme'
import { messageError } from 'reusable/mui/modals/message-error'
import { handleCircleSSO } from "reusable/utils/sso"

export default class MenuStore {
   sp
   sxProps = {} as SxProps<Theme>
   /** Whether the group is expanded. Purely user-controlled from here on (a real accordion) — only the
    *  initial value defaults to open when a sub-item happens to be active on mount. */
   userExpanded = false

   constructor(sp: { path: string, child: MenuChildItem, routerStore: RouterStore, mainContainerStore: MainContainerStore }) {
      makeAutoObservable(this)
      this.sp = sp
      this.userExpanded = this.hasActiveChild
   }

   get isSelected() {
      // A group item never highlights itself — only the active sub-item is highlighted.
      if (this.sp.child.children?.length) {
         return false
      }
      const action = this.sp.child.action
      const path = action && 'route' in action ? action.route.path : ''
      return this.activeRoute(this.sp.path, path)
   }

   /** True when the current page is one of this group's sub-routes. */
   get hasActiveChild() {
      return this.sp.child.children?.some(subItem => this.activeRoute(this.sp.path, subItem.navPath)) ?? false
   }

   get expanded() {
      return this.userExpanded
   }

   toggleExpanded = () => {
      this.userExpanded = !this.userExpanded
   }

   isSubItemSelected = (navPath: string) => this.activeRoute(this.sp.path, navPath)

   // Sub-item identity is threaded via a `data-nav-path` attribute on the clicked row (no inline handlers).
   navigateToSubItem = (event: MouseEvent<HTMLElement>) => {
      const navPath = event.currentTarget.dataset.navPath
      if (!navPath) {
         return
      }
      const accessIsRevoked = this.sp.mainContainerStore.sp.orgSubscriptionTierStore.handleRevokedAccess()
      if (accessIsRevoked) {
         return
      }
      this.sp.routerStore.navigate(navPath)
   }

   updatePath = (path: string) => {
      this.sp = { ...this.sp, path }
   }

   setSxProps = () => {
      if (this.isSelected) {
         this.sxProps = {
            '&::before': {
               content: '""',
               position: 'absolute',
               left: 0,
               right: 0,
               top: 0,
               bottom: 0,
               backgroundColor: 'rgba(255, 255, 255, 0.16)',
               borderRadius: '8px',
               overflow: 'hidden'
            }
         }
      } else {
         this.sxProps = {
            ':hover': { backgroundColor: themeConfig.dark.action.hover }, borderRadius: '8px',
         }
      }
   }

   activeRoute = (path: string, routeName?: string) => {
      if (!routeName) {
         return false
      }
      // Active when the current path is this item's page or a sub-route of it. Compare against the full
      // nav path (not just any matching segment) so a nested route that reuses a top-level name — e.g.
      // 'templates/meetings' — doesn't also activate the top-level 'meetings' item. The trailing-'/'
      // boundary still avoids false positives like 'coach' matching 'coach-report'.
      const navPath = this.sp.routerStore.getNavPath(routeName)
      return path === navPath || path.startsWith(`${navPath}/`)
   }

   handleClick = async () => {
      const action = this.sp.child.action
      if (!action) {
         return
      }

      // If the action is clicking on the clients menu item, then dont handle expiry
      if (!('route' in action && action.route.path === 'clients')) {
         const accessIsRevoked = this.sp.mainContainerStore.sp.orgSubscriptionTierStore.handleRevokedAccess()
         if (accessIsRevoked) {
            return
         }
      }

      if ('route' in action) {
         this.sp.routerStore.navigate(notNull(action.route.path))
      } else if ('type' in action && action.type === 'sso') {
         await this.handleSSO(action.provider)
      } else if ('modal' in action) {
         this.sp.mainContainerStore.menuComponentModal.component = action.modal
         if ('modalTitle' in action) {
            this.sp.mainContainerStore.menuComponentModal.title = action.modalTitle || ''
         } else {
            this.sp.mainContainerStore.menuComponentModal.title = this.sp.child.title
         }
         this.sp.mainContainerStore.menuComponentModal.state.open()
      }
   }

   /**
    * Handle SSO login for various providers
    */
   private async handleSSO(provider: 'circle') {
      switch (provider) {
         case 'circle':
            await handleCircleSSO(this.sp.mainContainerStore.sp.orgGuardStore.userData, 'Main menu')
            break
         default:
            await messageError('SSO Error', `SSO provider "${provider}" is not supported.`)
      }
   }
}
